"use client";

import { c as _c } from "react/compiler-runtime";
import { CloseBadgeIcon } from "@/components/common/close-badge-icon";
import { cn } from "@/utils/cn";
import Link from "next/link";
import { useState } from "react";
import { ProgressStepItem } from "./progress-step-item";
interface ProgressStepperProps {
  currentStep: number;
  steps: {
    id: number;
    title: string;
    key: string;
  }[];
  onClose?: () => void;
  data?: Record<string, unknown>;
  rawUnitId?: string | null;
  sellerUnitId?: string | null;
  urlGroupId?: string | null;
  info?: Record<string, unknown>;
  onStepClick?: (stepNumber: number) => void;
}
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce((acc: unknown, key: string): unknown => {
    if (acc && typeof acc === "object" && key) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj as unknown);
}

// Check if a field value is considered filled
function isFieldFilled(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0) && !(typeof value === "object" && Object.keys(value as Record<string, unknown>).length === 0);
}

// Check if a conditional field should be included in the form at all
function shouldFieldBeIncluded(field: {
  name: string;
  conditionalRequirement?: {
    dependsOn: string;
    whenValue?: unknown;
    whenExists?: boolean;
  };
}, data: Record<string, unknown>): boolean {
  if (!field.conditionalRequirement) return true;
  const {
    dependsOn,
    whenValue
  } = field.conditionalRequirement;
  const dependentValue = getNestedValue(data, dependsOn);
  if (whenValue !== undefined) {
    if (Array.isArray(whenValue)) return whenValue.includes(dependentValue);
    return dependentValue === whenValue;
  }
  return true;
}
interface StepProgressInfo {
  percent: number;
  requiredPercent: number;
  filledCount: number;
  totalCount: number;
  requiredFilledCount: number;
  requiredTotalCount: number;
  optionalFilledCount: number;
  optionalTotalCount: number;
}
export function getStepPercent(data: Record<string, unknown> = {}, info: Array<{
  name: string;
  required?: boolean;
  conditionalRequirement?: {
    dependsOn: string;
    whenValue?: unknown;
    whenExists?: boolean;
  };
}> = []): StepProgressInfo {
  let filledCount = 0;
  let totalCount = 0;
  let requiredFilledCount = 0;
  let requiredTotalCount = 0;
  let optionalFilledCount = 0;
  let optionalTotalCount = 0;
  for (const field of info) {
    // Skip fields that shouldn't be included based on conditional requirements
    if (!shouldFieldBeIncluded(field, data)) {
      continue;
    }
    totalCount++;
    const value = getNestedValue(data, field.name);
    const isFilled = isFieldFilled(value);
    if (isFilled) {
      filledCount++;
    }

    // Check if field is required (must be explicitly marked as required)
    const isRequired = Boolean(field.required);
    if (isRequired) {
      requiredTotalCount++;
      if (isFilled) {
        requiredFilledCount++;
      }
    } else {
      optionalTotalCount++;
      if (isFilled) {
        optionalFilledCount++;
      }
    }
  }
  const percent = totalCount > 0 ? Math.round(filledCount / totalCount * 100) : 0;
  const requiredPercent = requiredTotalCount > 0 ? Math.round(requiredFilledCount / requiredTotalCount * 100) : 0;
  return {
    percent,
    requiredPercent,
    filledCount,
    totalCount,
    requiredFilledCount,
    requiredTotalCount,
    optionalFilledCount,
    optionalTotalCount
  };
}
export function ProgressStepper(t0) {
  const $ = _c(33);
  const {
    steps: t1,
    currentStep,
    onClose,
    data: t2,
    rawUnitId,
    sellerUnitId,
    urlGroupId,
    info: t3,
    onStepClick
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const steps = t4;
  let t5;
  if ($[2] !== t2) {
    t5 = t2 === undefined ? {} : t2;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const data = t5;
  let t6;
  if ($[4] !== t3) {
    t6 = t3 === undefined ? {} : t3;
    $[4] = t3;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const info = t6;
  const [reverse, setReverse] = useState(false);
  let t7;
  if ($[6] !== onClose) {
    t7 = () => {
      setReverse(true);
      setTimeout(() => {
        setReverse(false);
      }, 300);
      onClose?.();
    };
    $[6] = onClose;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const handleClose = t7;
  let t8;
  if ($[8] !== currentStep || $[9] !== data || $[10] !== handleClose || $[11] !== info || $[12] !== onClose || $[13] !== onStepClick || $[14] !== rawUnitId || $[15] !== sellerUnitId || $[16] !== steps || $[17] !== urlGroupId) {
    t8 = () => <div className={cn("h-full bg-white flex flex-col", onClose && "transition-transform duration-300 ease-out")}>{onClose && <div className="flex items-center justify-between p-4 border-b border-neutral-200"><h2 className="text-lg font-semibold text-neutral-900">Progress</h2><button onClick={handleClose} className="p-1 rounded-full hover:bg-neutral-100 transition-colors" aria-label="Close"><CloseBadgeIcon className="w-6 h-6 text-neutral-500" /></button></div>}{!onClose && <Link href="/dashboard/seller" className="flex items-center gap-2 p-4 text-sm text-neutral-600 hover:text-neutral-900 transition-colors border-b border-neutral-200"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>Return to dashboard</Link>}<div className={!onClose ? "flex-1 overflow-y-auto scrollbar-thin p-3" : "flex-1 overflow-y-auto w-full max-w-md mx-auto scrollbar-hide p-2"}><h2 className="text-heading-3-semibold text-neutral-900 mb-4">Post your property</h2><div className="relative"><div className="space-y-4 relative">{steps.map((step, index) => <ProgressStepItem key={step.id} step={step} currentStep={currentStep} data={data} info={info} rawUnitId={rawUnitId} sellerUnitId={sellerUnitId} urlGroupId={urlGroupId} isLastStep={index === steps.length - 1} onStepClick={onStepClick} />)}</div></div></div></div>;
    $[8] = currentStep;
    $[9] = data;
    $[10] = handleClose;
    $[11] = info;
    $[12] = onClose;
    $[13] = onStepClick;
    $[14] = rawUnitId;
    $[15] = sellerUnitId;
    $[16] = steps;
    $[17] = urlGroupId;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const Content = t8;
  if (onClose) {
    const t9 = reverse ? "opacity-0" : "opacity-100";
    let t10;
    if ($[19] !== t9) {
      t10 = cn("fixed inset-0 z-50 bg-black/50 transition-opacity duration-300 px-2", t9);
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const t11 = reverse ? "translate-x-full" : "translate-x-0";
    let t12;
    if ($[21] !== t11) {
      t12 = cn("h-full transition-transform duration-300 ease-out", t11);
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] !== Content) {
      t13 = <Content />;
      $[23] = Content;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== t12 || $[26] !== t13) {
      t14 = <div className={t12}>{t13}</div>;
      $[25] = t12;
      $[26] = t13;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== t10 || $[29] !== t14) {
      t15 = <div className={t10}>{t14}</div>;
      $[28] = t10;
      $[29] = t14;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    return t15;
  }
  let t9;
  if ($[31] !== Content) {
    t9 = <Content />;
    $[31] = Content;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}